import { computed, inject, type Ref } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { groupsApi } from '@/api/groups';
import type { SerializedUser } from '@/api/users';

const MIN_GROUPS_TO_SHOW_FILTER = 2;
const FIVE_MINUTES_IN_MS = 300_000;

// Single source of truth for whether the group filter (and its title) should be
// shown: the company must have groups enabled and the user must see at least two
// of them — filtering by group is meaningless with one. The query key matches
// group-filter-select so TanStack Query dedupes (no extra request).
export function useGroupFilterVisible() {
  const currentUser = inject<Ref<SerializedUser>>('currentUser');
  const groupsEnabled = computed(() => !!currentUser?.value?.companyGroupsEnabled);

  const { data: groups } = useQuery({
    queryKey: ['groups', 'forFilters'],
    queryFn: () => groupsApi.indexForFilters(),
    staleTime: FIVE_MINUTES_IN_MS,
    enabled: groupsEnabled,
  });

  const shouldShow = computed(() =>
    groupsEnabled.value && (groups.value?.length ?? 0) >= MIN_GROUPS_TO_SHOW_FILTER,
  );

  return { shouldShow, groups, groupsEnabled };
}
